import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === "string" && search.next.startsWith("/") ? search.next : undefined,
  }),
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/auth/` }],
    meta: [
      { title: "Sign in | Leadership Edge Live" },
      {
        name: "description",
        content: "Get an email link that signs you in to your 360 Leader Behavior Check.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Sign in | Leadership Edge Live" },
      {
        property: "og:description",
        content: "Get an email link that signs you in to your 360 Leader Behavior Check.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const destination = next ?? "/my-review/";

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
        navigate({ to: destination, replace: true });
      }
    });
    return () => data.subscription.unsubscribe();
  }, [destination, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}${destination}`,
        },
      });
      if (otpError) throw otpError;
      setMessage(`Check ${email} for your sign-in link.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't send the link just now. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero
        maxWidth="6xl"
        headline={
          <>
            Sign in to your <span className="text-[#693D94]">360 check.</span>
          </>
        }
        supporting="Enter the email you started with. We send a link that signs you in. No password."
        ctas={[]}
      />
      <Section tone="default">
        <div className="max-w-md rounded-2xl border border-border/60 bg-background p-7">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="auth-email" className="text-sm font-semibold text-ink">
                Work email
              </Label>
              <Input
                id="auth-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2"
              />
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {message ? <p className="text-sm text-muted-foreground">{message}</p> : null}

            <button
              type="submit"
              disabled={busy}
              className="inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? "Sending..." : "Email me a sign-in link"}
            </button>
          </form>
        </div>
      </Section>
    </>
  );
}
